<template>
    <div>
        <div v-if="envelopeCanBeResent">
            <div class="h5 mb-3">
                {{ resendESignatureNotificationsHeader }}
            </div>
            <div class="mb-3">
                {{ infoMsg }}
            </div>
            <div>
                {{ resendESignatureNotificationsListHeader }}
            </div>
            <div class="mb-3">
                <div
                    v-for="recipient in resendableRecipients"
                    v-bind:key="recipient.PublicId">
                    <manage-request-recipient-control
                        v-bind:recipient="recipient"
                        v-bind:recipient-definition="recipientDefinitionsById[recipient.DefinitionId]"
                        v-bind:allow-corrections="allowCorrections"
                        class="mt-2"
                        v-on:start-edit="editingCount++"
                        v-on:end-edit="editingCount--"
                        v-on:update-contact-info="onUpdateContactInfo(recipient, $event)" />
                </div>
            </div>
            <div class="fst-italic mb-3">
                <span>
                    {{ resendESignatureNotificationsUpdatedDocumentWarning }}
                </span>
                <span>
                    <pc-help-icon v-on:click="toggleHelp(resendESignatureNotificationsUpdatedDocumentHelpMsg)" />
                </span>
            </div>
        </div>
        <div
            v-if="!envelopeCanBeResent"
            class="mb-3">
            <span>
                {{ noDocumentsResendableMsgWithBuffer }}
            </span>
            <span>
                <pc-help-icon v-on:click="toggleHelp(resendLimitationsHelpMsgWithBuffer)" />
            </span>
        </div>
        <pc-alert
            v-model:shown="showResendHelp"
            v-bind:message="resendHelpMessage"
            dismissible />
        <div class="d-flex justify-content-between">
            <pc-button
                id="OtherOptionsButton"
                class="px-0"
                link
                v-on:click="toggleOtherOptions">
                {{ otherOptionsText }}
            </pc-button>
            <div>
                <pc-button
                    id="ResendNotificationsButton"
                    v-bind:disabled="!resendButtonEnabled"
                    v-on:click="onResend">
                    {{ resendNotificationsButton }}
                </pc-button>
            </div>
        </div>
        <pc-transition transition="slide">
            <div v-if="showOtherOptions">
                <hr class="mx-n3" />
                <div class="h5 mb-3">
                    {{ cancelESignaturesHeader }}
                </div>
                <pc-radio-group
                    id="CancelRequestReasonSelector"
                    v-model="selectedCancelOption"
                    v-bind:options="cancelOptions"
                    v-bind:validator="requiredValidator"
                    name="selectedCancelOption"
                    v-bind:label="selectCancelReasonLabel"
                    required
                    class="mb-3" />
                <pc-input
                    v-model="additionalComments"
                    v-bind:required="selectedOther"
                    v-bind:validator="additionalCommentsValidator"
                    v-bind:label="additionalCommentsLabel"
                    v-on:validated="onValidated" />
                <pc-alert
                    class="mt-3"
                    v-bind:shown="showCancelDeterrent"
                    v-bind:severity="cancelDeterrentAlertSeverity"
                    v-bind:message="cancelDeterrentMessage" />
                <div class="d-flex align-items-center justify-content-end mt-3">
                    <pc-button
                        id="CancelAllDocumentsButton"
                        v-on:click="onCancel">
                        {{ cancelAllDocumentsButton }}
                    </pc-button>
                </div>
            </div>
        </pc-transition>
    </div>
</template>
<script>
    import { ALERT_SEVERITY } from "Util/alerts";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { ValidationHub } from "Validation/validationHub";
    import {
        ESIGNATURE_CANCEL_EXPLANATION_TYPE,
        getESignatureCancelExplanationLabel,
        isRecipientStatusTerminal
    } from "ESignatures/eSignatureConstants";
    import manageRequestRecipientControl from "ESignatures/ManageRequest/manageRequestRecipientControl.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS as ES_KEYS, NAMESPACE as ES_NS } from "Forms/Components/Elements/i18n/eSignature.i18n";
    import { KEYS, NAMESPACE } from "ESignatures/ManageRequest/i18n/manageRequestDialog.i18n";

    const CANCEL_DETERRENT_MESSAGE_KEY_BY_EXPLANATION_TYPE = {
        [ESIGNATURE_CANCEL_EXPLANATION_TYPE.ResendingNotification]: ES_KEYS.cancelDeterrentResending,
        [ESIGNATURE_CANCEL_EXPLANATION_TYPE.CorrectingRecipientContactInfo]:
            ES_KEYS.cancelDeterrentCorrectingContactInfo
    };

    export default {
        components: { manageRequestRecipientControl },
        provide() {
            return {
                validationHub: this.validationHub,
                getDataFieldValueByIdFn: this.getDataFieldValueByIdFn,
                getUserInfoByLoginIdFn: this.getUserInfoByLoginIdFn
            };
        },
        props: {
            envelope: { type: Object, required: true },
            allowManualCorrections: { type: Boolean, required: false, default: false },
            /** Must be an object of type ClientESignatureProviderCapabilities */
            providerCapabilities: { type: Object, required: true },
            getDataFieldValueByIdFn: { type: Function, required: true },
            getUserInfoByLoginIdFn: { type: Function, required: true }
        },
        emits: ["resend-notifications", "cancel-all"],
        data() {
            return {
                showOtherOptions: false,
                selectedCancelOption: null,
                cancelDeterrentAlertSeverity: ALERT_SEVERITY.info,
                additionalComments: null,
                validationHub: new ValidationHub(),
                requiredValidator: getValidator(this.$app, VALIDATOR_TYPES.required),
                validated: false,
                showResendHelp: false,
                resendHelpMessage: null,
                cancelExplanationOptions: [
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.ResendingNotification,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.CorrectingRecipientContactInfo,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.UpdatingContents,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other
                ],
                contactInfoUpdatesByRecipientId: {},
                // Kind of a hack; count of recipients whose contact info are being currently
                // edited to block resending while recipient is being edited.
                editingCount: 0
            };
        },
        computed: {
            allRecipients() {
                return Object.values(this.envelope.Recipients);
            },
            resendableRecipients() {
                const incompleteRecipients = this.allRecipients.filter(
                    (r) => !isRecipientStatusTerminal(r.Status.Code)
                );
                if (this.envelope.Definition.RoutingEnabled) {
                    const recipientDefinitionIdToRecipientRoutingOrder = {};
                    for (const recipient in this.envelope.Definition.Recipients) {
                        recipientDefinitionIdToRecipientRoutingOrder[recipient] =
                            this.envelope.Definition.Recipients[recipient].RoutingOrder;
                    }
                    let lowestRoutingNum = Number.MAX_SAFE_INTEGER;
                    let incompleteRecipientsCurrentRoutingNum = [];
                    for (let i = 0; i < incompleteRecipients.length; i++) {
                        const recipientOrder =
                            recipientDefinitionIdToRecipientRoutingOrder[incompleteRecipients[i].DefinitionId];
                        if (recipientOrder < lowestRoutingNum) {
                            lowestRoutingNum = recipientOrder;
                            incompleteRecipientsCurrentRoutingNum = [incompleteRecipients[i]];
                        } else if (recipientOrder == lowestRoutingNum) {
                            incompleteRecipientsCurrentRoutingNum.push(incompleteRecipients[i]);
                        }
                    }
                    return incompleteRecipientsCurrentRoutingNum;
                }
                return incompleteRecipients;
            },
            recipientDefinitionsById() {
                return this.envelope.Definition.Recipients;
            },
            envelopeCanBeResent() {
                return this.envelope.CanBeResent;
            },
            resendButtonEnabled() {
                return this.envelopeCanBeResent && this.editingCount == 0;
            },
            allowCorrections() {
                return this.allowManualCorrections;
            },
            infoMsg() {
                return this.allowManualCorrections ? this.resendESignaturesAdminInfoMsg : this.resendESignaturesInfoMsg;
            },
            otherOptionsText() {
                return this.showOtherOptions ? this.hideLinkLabel : this.otherOptionsLinkLabel;
            },
            additionalCommentsValidator() {
                return getValidator(this.$app, [
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: () =>
                            Boolean(this.additionalComments) ||
                            this.selectedCancelOption != ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other,
                        errorMsg: this.additionalCommentsRequiredError
                    }
                ]);
            },
            additionalCommentsLabel() {
                return this.selectedOther ? this.provideAdditionalDetailLabel : this.anyAdditionalCommentsLabel;
            },
            cancelOptions() {
                const options = [];
                for (const option of this.cancelExplanationOptions) {
                    options.push({
                        text: getESignatureCancelExplanationLabel(this.$app, option),
                        value: option
                    });
                }
                return options;
            },
            selectedResendNotifications() {
                return this.selectedCancelOption == ESIGNATURE_CANCEL_EXPLANATION_TYPE.ResendingNotification;
            },
            selectedOther() {
                return this.selectedCancelOption == ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other;
            },
            showCancelDeterrent() {
                return Boolean(this.cancelDeterrentMessage);
            },
            cancelDeterrentMessage() {
                if (!CANCEL_DETERRENT_MESSAGE_KEY_BY_EXPLANATION_TYPE.hasOwnProperty(this.selectedCancelOption)) {
                    return null;
                }
                const messageKey = CANCEL_DETERRENT_MESSAGE_KEY_BY_EXPLANATION_TYPE[this.selectedCancelOption];
                // Choose appropriate i18n computed based on key that maps to reason.
                return this[messageKey];
            },
            ...mapI18n(
                {
                    ...ES_KEYS,
                    noDocumentsResendableMsgWithBuffer() {
                        return {
                            key: ES_KEYS.noDocumentsResendableMsg,
                            values: { bufferMinutes: this.providerCapabilities.RecipientResendThresholdInMinutes }
                        };
                    },
                    resendLimitationsHelpMsgWithBuffer() {
                        return {
                            key: ES_KEYS.resendLimitationsHelpMsg,
                            values: {
                                minutes: this.providerCapabilities.RecipientResendThresholdInMinutes
                            }
                        };
                    },
                    cancelDeterrentResending() {
                        return {
                            key: ES_KEYS.cancelDeterrentResending,
                            values: {
                                emailSubject: this.envelope.Definition.EmailSubject
                            }
                        };
                    }
                },
                ES_NS
            ),
            ...mapI18n({ ...KEYS }, NAMESPACE)
        },
        watch: {
            selectedCancelOption() {
                // this is a workaround for when the user selects other, touches the additional
                // comments field, then selects any other option. Without this watch, the
                // additional comments validation error would still appear
                if (this.validated) {
                    this.validationHub.validate();
                }
            }
        },
        methods: {
            onValidated() {
                this.validated = true;
            },
            toggleOtherOptions() {
                this.showOtherOptions = !this.showOtherOptions;
            },
            onResend() {
                this.$emit("resend-notifications", this.contactInfoUpdatesByRecipientId);
            },
            onCancel() {
                const validation = this.validationHub.validate();
                if (validation) {
                    return;
                }

                const voidData = {
                    explanationDetail: this.additionalComments,
                    explanationType: this.selectedCancelOption
                };
                this.$emit("cancel-all", voidData);
            },
            onUpdateContactInfo(recipient, updatedContactInfo) {
                this.contactInfoUpdatesByRecipientId[recipient.PublicId] = updatedContactInfo;
            },
            toggleHelp(message) {
                this.showResendHelp = !this.showResendHelp;
                this.resendHelpMessage = message;
            }
        }
    };
</script>
